import React, { useState, useEffect, useRef } from 'react';
import { groupSessionService, userService, authService } from '../api/services';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import {
  Users, Video, CreditCard, ToggleLeft, ToggleRight,
  Save, Loader2, AlertCircle, Settings, RefreshCw,
  Camera, Lock, Eye, EyeOff,
} from 'lucide-react';
import toast from 'react-hot-toast';

// Default settings shown even if DB is empty
const DEFAULT_SETTINGS = [
  { key: 'group_session_enabled',            value: 'true',             description: 'Whether group sessions are enabled on the platform' },
  { key: 'group_session_max_participants',   value: '10',               description: 'Maximum participants per group session (including host and expert)' },
  { key: 'group_session_code_prefix',        value: 'TOUR',             description: 'Prefix for generated join codes e.g. TOUR-4829' },
  { key: 'group_session_allowed_types',      value: 'VIDEO,AUDIO,CHAT', description: 'Allowed session types for group sessions' },
  { key: 'group_session_payment_enabled',    value: 'false',            description: 'Whether participants are charged per person for group sessions' },
  { key: 'group_session_payment_multiplier', value: '1',                description: 'Rate multiplier per participant (0.5 = 50% of expert rate per person)' },
];

const formatKey = (key) =>
  key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// ── Toggle Setting ────────────────────────────────────────────
const ToggleSetting = ({ label, description, value, onToggle }) => {
  const isOn = value === 'true';
  return (
    <div className="flex items-center justify-between py-4 border-b border-cream-100 last:border-0">
      <div className="flex-1 mr-6">
        <p className="text-sm font-medium text-forest-900">{label}</p>
        {description && <p className="text-xs text-forest-400 mt-0.5">{description}</p>}
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        <span className={`text-xs font-medium ${isOn ? 'text-green-600' : 'text-forest-400'}`}>
          {isOn ? 'Enabled' : 'Disabled'}
        </span>
        <button onClick={onToggle} className="transition-colors">
          {isOn
            ? <ToggleRight size={34} className="text-forest-600" />
            : <ToggleLeft  size={34} className="text-forest-400" />}
        </button>
      </div>
    </div>
  );
};

// ── Number/Text Setting ───────────────────────────────────────
const InputSetting = ({ label, description, value, onChange, onSave, saving, changed }) => (
  <div className="flex items-center justify-between py-4 border-b border-cream-100 last:border-0 gap-4">
    <div className="flex-1 min-w-0">
      <p className="text-sm font-medium text-forest-900">{label}</p>
      {description && <p className="text-xs text-forest-400 mt-0.5">{description}</p>}
    </div>
    <div className="flex items-center gap-2 flex-shrink-0">
      <input value={value} onChange={onChange}
        className="w-36 px-3 py-1.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 text-center" />
      {changed && (
        <button onClick={onSave} disabled={saving}
          className="flex items-center gap-1.5 text-xs font-medium bg-forest-800 text-cream-50 px-3 py-1.5 rounded-full hover:bg-forest-700 transition-colors disabled:opacity-60">
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
        </button>
      )}
    </div>
  </div>
);

// ── Section wrapper ──────────────────────────────────────────
const Section = ({ title, children }) => (
  <div className="bg-white border border-cream-200 rounded-2xl p-6 space-y-5">
    <h3 className="font-serif text-lg font-semibold text-forest-900 border-b border-cream-100 pb-3">{title}</h3>
    {children}
  </div>
);

const Field = ({ label, type = 'text', value, onChange, placeholder }) => (
  <div>
    <label className="block text-xs font-medium text-forest-700 mb-1.5">{label}</label>
    <input type={type} value={value ?? ''} onChange={onChange} placeholder={placeholder}
      className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white" />
  </div>
);

// ── Main Page ─────────────────────────────────────────────────
const AdminSettingsPage = () => {
  const { user }  = useAuth();
  const fileRef   = useRef(null);
  const initials  = `${user?.firstName?.[0] ?? ''}${user?.lastName?.[0] ?? ''}`;

  // ── Personal profile ─────────────────────────────────────────
  const [profile, setProfile]           = useState({ firstName: user?.firstName ?? '', lastName: user?.lastName ?? '', phone: user?.phone ?? '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const upd = (k) => (e) => setProfile(p => ({ ...p, [k]: e.target.value }));

  // ── Password ─────────────────────────────────────────────────
  const [pwd,      setPwd]      = useState({ current: '', new: '', confirm: '' });
  const [showPwd,  setShowPwd]  = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      await userService.updateProfile({ firstName: profile.firstName, lastName: profile.lastName, phone: profile.phone });
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not save profile');
    } finally { setSavingProfile(false); }
  };

  const changePassword = async () => {
    if (pwd.new !== pwd.confirm) { toast.error('New passwords do not match'); return; }
    if (pwd.new.length < 8)      { toast.error('Password must be at least 8 characters'); return; }
    setSavingPwd(true);
    try {
      await authService.changePassword(pwd.current, pwd.new);
      toast.success('Password changed!');
      setPwd({ current: '', new: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not change password');
    } finally { setSavingPwd(false); }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingAvatar(true);
    try {
      const fd = new FormData();
      fd.append('avatar', file);
      await userService.uploadAvatar(fd);
      toast.success('Avatar updated! Refresh to see changes.');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not upload avatar');
    } finally { setUploadingAvatar(false); e.target.value = ''; }
  };

  const [settings, setSettings] = useState({});
  const [original, setOriginal] = useState({});
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');
  const [saving,   setSaving]   = useState({});

  const loadSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const res  = await groupSessionService.getSettings();
      const list = res.data?.data?.settings ?? res.data?.settings ?? [];

      // Merge DB values with defaults
      const merged = {};
      DEFAULT_SETTINGS.forEach(d => { merged[d.key] = d.value; });
      list.forEach(s => { merged[s.key] = s.value; });

      setSettings(merged);
      setOriginal({ ...merged });
    } catch (err) {
      setError('Could not load settings from server. Showing defaults.');
      // Still show defaults so admin can save them
      const defaults = {};
      DEFAULT_SETTINGS.forEach(d => { defaults[d.key] = d.value; });
      setSettings(defaults);
      setOriginal({ ...defaults });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadSettings(); }, []);

  const handleToggle = async (key) => {
    const newVal = settings[key] === 'true' ? 'false' : 'true';
    setSettings(p => ({ ...p, [key]: newVal }));
    try {
      await groupSessionService.updateSetting(key, newVal);
      setOriginal(p => ({ ...p, [key]: newVal }));
      toast.success(`${formatKey(key)} ${newVal === 'true' ? 'enabled' : 'disabled'}`);
    } catch (err) {
      setSettings(p => ({ ...p, [key]: original[key] })); // revert
      toast.error('Could not save — make sure the migration has run');
    }
  };

  const handleChange = (key, val) => setSettings(p => ({ ...p, [key]: val }));

  const handleSave = async (key) => {
    setSaving(p => ({ ...p, [key]: true }));
    try {
      await groupSessionService.updateSetting(key, settings[key]);
      setOriginal(p => ({ ...p, [key]: settings[key] }));
      toast.success('Setting saved');
    } catch (err) {
      toast.error('Could not save — make sure the migration has run');
    } finally {
      setSaving(p => ({ ...p, [key]: false }));
    }
  };

  const isChanged = (key) => settings[key] !== original[key];

  // Categorise
  const accessKeys  = ['group_session_enabled', 'group_session_max_participants', 'group_session_code_prefix', 'group_session_allowed_types'];
  const paymentKeys = ['group_session_payment_enabled', 'group_session_payment_multiplier'];

  const renderSetting = (key) => {
    const def  = DEFAULT_SETTINGS.find(d => d.key === key);
    const val  = settings[key] ?? def?.value ?? '';
    const desc = def?.description ?? '';
    const label = formatKey(key);

    if (val === 'true' || val === 'false') {
      return (
        <ToggleSetting
          key={key} label={label} description={desc}
          value={val} onToggle={() => handleToggle(key)}
        />
      );
    }
    return (
      <InputSetting
        key={key} label={label} description={desc}
        value={val}
        onChange={e => handleChange(key, e.target.value)}
        onSave={() => handleSave(key)}
        saving={!!saving[key]}
        changed={isChanged(key)}
      />
    );
  };

  return (
    <DashboardLayout title="Platform Settings">
      <div className="max-w-3xl space-y-6">

        <div className="flex items-center justify-between">
          <p className="text-sm text-forest-500">Configure platform-wide settings. Changes take effect immediately.</p>
          <button onClick={loadSettings}
            className="p-2 text-forest-500 hover:text-forest-800 border border-cream-200 rounded-xl hover:bg-cream-50 transition-colors">
            <RefreshCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* ── Profile Photo ── */}
        <Section title="Profile Photo">
          <div className="flex items-center gap-5">
            <div className="relative">
              <div className="w-20 h-20 bg-forest-700 rounded-2xl flex items-center justify-center text-cream-50 font-serif font-bold text-2xl overflow-hidden">
                {user?.avatar
                  ? <img src={user.avatar} alt="" className="w-full h-full object-cover" />
                  : initials}
              </div>
              <button onClick={() => fileRef.current?.click()}
                className="absolute -bottom-2 -right-2 w-8 h-8 bg-forest-800 border-2 border-white rounded-full flex items-center justify-center text-cream-50 hover:bg-forest-700 transition-colors">
                {uploadingAvatar ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />}
              </button>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
            </div>
            <div>
              <p className="text-sm font-medium text-forest-800">{user?.firstName} {user?.lastName}</p>
              <p className="text-xs text-forest-500 mt-0.5">Super Admin</p>
              <p className="text-xs text-forest-400 mt-1">JPEG, PNG or WEBP · Max 5MB</p>
            </div>
          </div>
        </Section>

        {/* ── Basic Information ── */}
        <Section title="Basic Information">
          <div className="grid grid-cols-2 gap-4">
            <Field label="First Name" value={profile.firstName} onChange={upd('firstName')} placeholder="Admin" />
            <Field label="Last Name"  value={profile.lastName}  onChange={upd('lastName')}  placeholder="User" />
          </div>
          <Field label="Phone" type="tel" value={profile.phone} onChange={upd('phone')} placeholder="+233244000001" />
          <button onClick={saveProfile} disabled={savingProfile} className="btn-primary flex items-center gap-2 disabled:opacity-60">
            {savingProfile ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save Changes
          </button>
        </Section>

        {/* ── Change Password ── */}
        <Section title="Change Password">
          <div className="relative">
            <label className="block text-xs font-medium text-forest-700 mb-1.5">Current Password</label>
            <input type={showPwd ? 'text' : 'password'} value={pwd.current}
              onChange={e => setPwd(p => ({ ...p, current: e.target.value }))}
              placeholder="••••••••"
              className="w-full px-3.5 py-2.5 rounded-xl border border-cream-300 text-sm text-forest-900 focus:outline-none focus:border-forest-500 bg-white pr-10" />
            <button type="button" onClick={() => setShowPwd(!showPwd)}
              className="absolute right-3 top-8 text-forest-400 hover:text-forest-700">
              {showPwd ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="New Password"     type={showPwd ? 'text' : 'password'} value={pwd.new}     onChange={e => setPwd(p => ({ ...p, new: e.target.value }))}     placeholder="••••••••" />
            <Field label="Confirm Password" type={showPwd ? 'text' : 'password'} value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} placeholder="••••••••" />
          </div>
          <button onClick={changePassword} disabled={savingPwd || !pwd.current || !pwd.new}
            className="btn-primary flex items-center gap-2 disabled:opacity-60">
            {savingPwd ? <Loader2 size={14} className="animate-spin" /> : <Lock size={14} />} Change Password
          </button>
        </Section>

        {/* ── Divider ── */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-cream-200" />
          <span className="text-xs font-semibold text-forest-400 uppercase tracking-widest">Platform Settings</span>
          <div className="flex-1 h-px bg-cream-200" />
        </div>

        {error && (
          <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <AlertCircle size={16} className="text-amber-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-amber-800">{error}</p>
              <p className="text-xs text-amber-700 mt-1">
                Run <code className="bg-amber-100 px-1.5 py-0.5 rounded font-mono">node prisma/seed-settings.js</code> to initialise settings in the database.
              </p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1,2].map(i => <div key={i} className="h-48 bg-white border border-cream-200 rounded-2xl animate-pulse" />)}
          </div>
        ) : (
          <>
            {/* Group Sessions — Access & Limits */}
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-cream-100">
                <div className="w-10 h-10 bg-forest-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Users size={18} className="text-forest-600" />
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-forest-900">Group Sessions — Access & Limits</h3>
                  <p className="text-xs text-forest-400 mt-0.5">Control who can use group sessions and how many people can join</p>
                </div>
              </div>
              <div className="px-5">
                {accessKeys.map(renderSetting)}
              </div>
            </div>

            {/* Group Sessions — Payment */}
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-cream-100">
                <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CreditCard size={18} className="text-amber-600" />
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-forest-900">Group Sessions — Payment</h3>
                  <p className="text-xs text-forest-400 mt-0.5">Configure per-person payment. Enable when ready to charge participants.</p>
                </div>
              </div>
              <div className="px-5">
                {paymentKeys.map(renderSetting)}
              </div>
            </div>

            {/* Video Infrastructure */}
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-cream-100">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Video size={18} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-forest-900">Video Infrastructure</h3>
                  <p className="text-xs text-forest-400 mt-0.5">Daily.co — managed via environment variables</p>
                </div>
              </div>
              <div className="p-5 space-y-3">
                {[
                  { label: 'Provider',      value: 'Daily.co' },
                  { label: 'Max Group Size', value: `${settings['group_session_max_participants'] ?? 10} participants` },
                  { label: 'Session Types',  value: settings['group_session_allowed_types'] ?? 'VIDEO, AUDIO, CHAT' },
                  { label: 'API Key',        value: '••••••••••••••••••••••' },
                ].map(({ label, value }) => (
                  <div key={label} className="flex items-center justify-between py-1">
                    <span className="text-xs text-forest-500">{label}</span>
                    <span className="text-xs font-medium text-forest-800 font-mono">{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Communities */}
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-cream-100">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-forest-900">Communities</h3>
                  <p className="text-xs text-forest-400 mt-0.5">Control group chat and community settings</p>
                </div>
              </div>
              <div className="px-5">
                {renderSetting('community_enabled')}
                {renderSetting('community_max_members')}
              </div>
            </div>

            {/* Level Access Control */}
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-cream-100">
                <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-purple-600">
                    <rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-forest-900">Level Access Control</h3>
                  <p className="text-xs text-forest-400 mt-0.5">Control whether clients must complete levels in order or can access all levels freely</p>
                </div>
              </div>
              <div className="px-5">
                {renderSetting('level_locking_enabled')}
              </div>
              <div className="px-5 pb-4">
                <div className={`rounded-xl px-4 py-3 text-xs leading-relaxed ${
                  settings['level_locking_enabled'] === 'true'
                    ? 'bg-amber-50 border border-amber-100 text-amber-700'
                    : 'bg-green-50 border border-green-100 text-green-700'
                }`}>
                  {settings['level_locking_enabled'] === 'true' ? (
                    <span>🔒 <strong>Sequential mode:</strong> Clients must complete all requirements of each level (content, session, canvas) before the next level automatically unlocks.</span>
                  ) : (
                    <span>🔓 <strong>Open mode:</strong> All levels are accessible to all clients immediately. No completion required to progress.</span>
                  )}
                </div>
              </div>
            </div>

            {/* Communities */}
            <div className="bg-white border border-cream-200 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-3 p-5 border-b border-cream-100">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                </div>
                <div>
                  <h3 className="font-serif font-semibold text-forest-900">Communities</h3>
                  <p className="text-xs text-forest-400 mt-0.5">Control group chat and video call communities</p>
                </div>
              </div>
              <div className="px-5">
                {['community_enabled', 'community_max_members'].map(renderSetting)}
              </div>
            </div>

            {/* Payment info box */}
            {settings['group_session_payment_enabled'] !== 'true' && (
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5 flex items-start gap-3">
                <CreditCard size={18} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-amber-800 mb-1">Payment not yet enabled</p>
                  <p className="text-xs text-amber-700 leading-relaxed">
                    Toggle <strong>Group Session Payment Enabled</strong> to ON when you're ready to charge participants.
                    The <strong>Payment Multiplier</strong> sets the per-person rate relative to the expert's hourly rate
                    (e.g. <code className="bg-amber-100 px-1 rounded">0.5</code> = 50% of rate per person).
                  </p>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default AdminSettingsPage;